import React, { useState, useEffect } from "react";
import * as S from "./AdminSignupRequest.styled";
import logo from "assets/logo.png";
import { adminAuthService } from "services/adminAuthService";
import { universityService, University } from "services/universityService";

interface FormValues {
  loginId: string;
  name: string;
  password: string;
  passwordConfirm: string;
  universityId: string;
}

interface FormErrors {
  loginId?: string;
  name?: string;
  password?: string;
  passwordConfirm?: string;
  universityId?: string;
}

const AdminSignupRequest: React.FC = () => {
  const [form, setForm] = useState<FormValues>({
    loginId: "",
    name: "",
    password: "",
    passwordConfirm: "",
    universityId: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [universities, setUniversities] = useState<University[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [submitError, setSubmitError] = useState("");

  useEffect(() => {
    universityService.getUniversities().then(setUniversities).catch(() => {});
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validate = (): boolean => {
    const next: FormErrors = {};

    if (!form.loginId.trim()) {
      next.loginId = "아이디를 입력해주세요.";
    } else if (form.loginId.length < 4) {
      next.loginId = "아이디는 4자 이상이어야 합니다.";
    }

    if (!form.name.trim()) {
      next.name = "이름을 입력해주세요.";
    }

    if (!form.password) {
      next.password = "비밀번호를 입력해주세요.";
    } else if (form.password.length < 8) {
      next.password = "비밀번호는 8자 이상이어야 합니다.";
    }

    if (!form.passwordConfirm) {
      next.passwordConfirm = "비밀번호 확인을 입력해주세요.";
    } else if (form.password !== form.passwordConfirm) {
      next.passwordConfirm = "비밀번호가 일치하지 않습니다.";
    }

    if (!form.universityId) {
      next.universityId = "소속 대학교를 선택해주세요.";
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    setIsLoading(true);
    setSubmitError("");

    try {
      await adminAuthService.register({
        loginId: form.loginId,
        name: form.name,
        password: form.password,
        universityId: Number(form.universityId),
      });
      setIsSuccess(true);
    } catch (error: any) {
      if (error.response?.status === 409) {
        setSubmitError("이미 사용 중인 아이디입니다.");
      } else {
        setSubmitError("가입 요청에 실패했습니다. 다시 시도해주세요.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <S.Page>
      {/* ── 좌측 패널 ── */}
      <S.LeftPanel>
        <S.LeftTop>
          <S.LogoImg src={logo} alt="지금 우리 학교는" />
          <S.LeftTitle>
            관리자 계정을<br />
            <span>요청</span>하세요
          </S.LeftTitle>
          <S.LeftDesc>
            가입 요청 후 시스템 승인이 완료되면<br />
            관리자 포털을 이용할 수 있습니다.
          </S.LeftDesc>
        </S.LeftTop>

        <S.LeftBottom>
          <S.LeftNotice>
            <strong>승인 안내</strong><br />
            가입 요청은 담당자 검토 후 승인됩니다.
            승인 완료 시 등록하신 아이디로 로그인하실 수 있습니다.
          </S.LeftNotice>
        </S.LeftBottom>
      </S.LeftPanel>

      {/* ── 우측 폼 ── */}
      <S.RightPanel>
        {isSuccess ? (
          <S.SuccessCard>
            <S.SuccessIcon>✅</S.SuccessIcon>
            <S.SuccessTitle>가입 요청이 완료됐어요</S.SuccessTitle>
            <S.SuccessDesc>
              담당자 검토 후 승인이 진행됩니다.<br />
              승인 완료 후 로그인하실 수 있습니다.
            </S.SuccessDesc>
            <S.SuccessButton to="/admin/login">로그인 페이지로 이동</S.SuccessButton>
          </S.SuccessCard>
        ) : (
          <S.FormCard>
            <S.FormTitle>관리자 가입 요청</S.FormTitle>
            <S.FormSubtitle>아래 정보를 입력하고 가입을 요청하세요</S.FormSubtitle>

            <S.FieldGroup>
              <S.Field>
                <S.FieldLabel htmlFor="loginId">아이디</S.FieldLabel>
                <S.Input
                  id="loginId"
                  name="loginId"
                  type="text"
                  placeholder="사용할 아이디를 입력하세요 (4자 이상)"
                  value={form.loginId}
                  onChange={handleChange}
                  hasError={!!errors.loginId}
                  autoComplete="username"
                />
                {errors.loginId && <S.FieldError>{errors.loginId}</S.FieldError>}
              </S.Field>

              <S.Field>
                <S.FieldLabel htmlFor="name">이름</S.FieldLabel>
                <S.Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="실명을 입력하세요"
                  value={form.name}
                  onChange={handleChange}
                  hasError={!!errors.name}
                  autoComplete="name"
                />
                {errors.name && <S.FieldError>{errors.name}</S.FieldError>}
              </S.Field>

              <S.Field>
                <S.FieldLabel htmlFor="universityId">소속 대학교</S.FieldLabel>
                <S.Select
                  id="universityId"
                  name="universityId"
                  value={form.universityId}
                  onChange={handleChange}
                  hasError={!!errors.universityId}
                >
                  <option value="">대학교를 선택하세요</option>
                  {universities.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name}
                    </option>
                  ))}
                </S.Select>
                {errors.universityId && <S.FieldError>{errors.universityId}</S.FieldError>}
              </S.Field>

              <S.Field>
                <S.FieldLabel htmlFor="password">비밀번호</S.FieldLabel>
                <S.Input
                  id="password"
                  name="password"
                  type="password"
                  placeholder="8자 이상 입력하세요"
                  value={form.password}
                  onChange={handleChange}
                  hasError={!!errors.password}
                  autoComplete="new-password"
                />
                {errors.password && <S.FieldError>{errors.password}</S.FieldError>}
              </S.Field>

              <S.Field>
                <S.FieldLabel htmlFor="passwordConfirm">비밀번호 확인</S.FieldLabel>
                <S.Input
                  id="passwordConfirm"
                  name="passwordConfirm"
                  type="password"
                  placeholder="비밀번호를 다시 입력하세요"
                  value={form.passwordConfirm}
                  onChange={handleChange}
                  hasError={!!errors.passwordConfirm}
                  autoComplete="new-password"
                />
                {errors.passwordConfirm && (
                  <S.FieldError>{errors.passwordConfirm}</S.FieldError>
                )}
              </S.Field>

              {submitError && <S.FieldError>{submitError}</S.FieldError>}
            </S.FieldGroup>

            <S.SubmitButton onClick={handleSubmit} disabled={isLoading}>
              {isLoading ? "요청 중..." : "가입 요청하기"}
            </S.SubmitButton>

            <S.BackLink to="/admin/login">← 관리자 로그인으로 돌아가기</S.BackLink>
          </S.FormCard>
        )}
      </S.RightPanel>
    </S.Page>
  );
};

export default AdminSignupRequest;
